import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  FileText, Clock, CheckCircle, XCircle, AlertTriangle,
  Upload, Building, ChevronRight, Loader2
} from 'lucide-react';
import { api } from '../services/api';
import { useAuth } from '../context/AuthContext';


const statusConfig = {
  PENDING: { icon: Clock, color: 'text-yellow-500', bg: 'bg-yellow-100', label: 'Pendente' },
  CODE_SENT: { icon: Clock, color: 'text-blue-500', bg: 'bg-blue-100', label: 'Aguardando código' },
  DOCUMENT_PENDING: { icon: Upload, color: 'text-orange-500', bg: 'bg-orange-100', label: 'Aguardando documento' },
  DISPUTE: { icon: AlertTriangle, color: 'text-purple-500', bg: 'bg-purple-100', label: 'Em análise' },
  VERIFIED: { icon: CheckCircle, color: 'text-green-500', bg: 'bg-green-100', label: 'Aprovada' },
  REJECTED: { icon: XCircle, color: 'text-red-500', bg: 'bg-red-100', label: 'Rejeitada' }
};

export default function MinhasReivindicacoes() {
  const navigate = useNavigate();
  const { user, token } = useAuth();
  const [claims, setClaims] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedClaim, setExpandedClaim] = useState(null);
  
  // Document upload
  const [documentFile, setDocumentFile] = useState(null);
  const [documentType, setDocumentType] = useState('CNPJ');
  const [documentDescription, setDocumentDescription] = useState('');
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    if (!user) {
      navigate('/login');
      return;
    }
    fetchClaims();
  }, [user, navigate]);

  const fetchClaims = async () => {
    try {
      setLoading(true);
      const res = await api.get('/my-claims');
      setClaims(res.data);
    } catch (error) {
      console.error('Erro ao buscar reivindicações:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDocumentUpload = async (claimId, businessId) => {
    if (!documentFile) {
      alert('Selecione um arquivo');
      return;
    }

    setUploading(true);
    try {
      const base64 = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result);
        reader.onerror = reject;
        reader.readAsDataURL(documentFile);
      });

      await api.post(
        `/businesses/${businessId}/claim/${claimId}/upload-document`,
        {
          document_type: documentType,
          document_description: documentDescription,
          document_base64: base64
        }
      );

      alert('Documento enviado com sucesso!');
      setDocumentFile(null);
      setDocumentDescription('');
      setExpandedClaim(null);
      fetchClaims();
    } catch (error) {
      alert(error.response?.data?.detail || 'Erro ao enviar documento');
    } finally {
      setUploading(false);
    }
  };

  if (!user) return null;

  return (
    <div className="min-h-screen bg-[var(--bg-secondary)]">
      {/* Header */}
      <div className="bg-[var(--bg-primary)] border-b border-[var(--border)]">
        <div className="max-w-4xl mx-auto px-4 py-6">
          <div className="flex items-center gap-3">
            <div className="p-3 bg-[var(--primary)]/10 rounded-xl">
              <FileText className="w-6 h-6 text-[var(--primary)]" />
            </div>
            <div>
              <h1 className="text-2xl font-bold">Minhas Reivindicações</h1>
              <p className="text-sm text-[var(--text-secondary)]">
                Acompanhe o status das suas solicitações de propriedade
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-4 py-6">
        {loading ? (
          <div className="flex justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-[var(--primary)]" />
          </div>
        ) : claims.length === 0 ? (
          <div className="bg-[var(--bg-primary)] rounded-xl p-12 text-center">
            <FileText className="w-16 h-16 text-[var(--text-muted)] mx-auto mb-4" />
            <h2 className="text-xl font-bold mb-2">Nenhuma reivindicação</h2>
            <p className="text-[var(--text-secondary)]">
              Você ainda não solicitou a propriedade de nenhum negócio.
            </p>
          </div>
        ) : (
          <div className="space-y-4">
            {claims.map((claim) => {
              const status = statusConfig[claim.status] || statusConfig.PENDING;
              const StatusIcon = status.icon;
              const isExpanded = expandedClaim === claim.id;

              return (
                <div
                  key={claim.id}
                  className="bg-[var(--bg-primary)] rounded-xl border border-[var(--border)] overflow-hidden"
                >
                  {/* Claim Header */}
                  <div
                    className="p-4 cursor-pointer hover:bg-gray-50 transition-colors"
                    onClick={() => setExpandedClaim(isExpanded ? null : claim.id)}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className={`p-2 rounded-lg ${status.bg}`}>
                          <StatusIcon className={`w-5 h-5 ${status.color}`} />
                        </div>
                        <div>
                          <h3 className="font-bold">{claim.business_name}</h3>
                          <p className="text-xs text-[var(--text-muted)]">
                            Solicitado em: {new Date(claim.created_at).toLocaleDateString('pt-BR')}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        <span className={`px-3 py-1 rounded-full text-xs font-medium ${status.bg} ${status.color}`}>
                          {status.label}
                        </span>
                        <ChevronRight className={`w-5 h-5 transition-transform ${isExpanded ? 'rotate-90' : ''}`} />
                      </div>
                    </div>
                  </div>

                  {/* Expanded Content */}
                  {isExpanded && (
                    <div className="px-4 pb-4 border-t border-[var(--border)]">
                      <div className="pt-4 space-y-4">
                        {/* Status Details */}
                        <div className="bg-gray-50 rounded-lg p-4">
                          <h4 className="font-medium mb-2">Detalhes da reivindicação</h4>
                          <div className="text-sm space-y-1">
                            <p><strong>Método:</strong> {claim.verification_method}</p>
                            {claim.document_type && (
                              <p><strong>Documento:</strong> {claim.document_type}</p>
                            )}
                            {claim.rejection_reason && (
                              <p className="text-red-600"><strong>Motivo:</strong> {claim.rejection_reason}</p>
                            )}
                          </div>
                        </div>

                        {/* Document Upload Form (for DOCUMENT_PENDING status) */}
                        {claim.status === 'DOCUMENT_PENDING' && (
                          <div className="bg-orange-50 border border-orange-200 rounded-xl p-4">
                            <h4 className="font-medium text-orange-800 mb-3">
                              Envie um documento comprobatório
                            </h4>
                            <div className="space-y-3">
                              <div>
                                <label className="block text-sm font-medium mb-1">Tipo de documento</label>
                                <select
                                  value={documentType}
                                  onChange={(e) => setDocumentType(e.target.value)}
                                  className="w-full px-3 py-2 border rounded-lg"
                                >
                                  <option value="CNPJ">Cartão CNPJ</option>
                                  <option value="CONTRATO_SOCIAL">Contrato Social</option>
                                  <option value="ALVARA">Alvará de Funcionamento</option>
                                  <option value="OUTRO">Outro</option>
                                </select>
                              </div>
                              <div>
                                <label className="block text-sm font-medium mb-1">Arquivo (PDF ou imagem)</label>
                                <input
                                  type="file"
                                  accept=".pdf,.jpg,.jpeg,.png"
                                  onChange={(e) => setDocumentFile(e.target.files[0])}
                                  className="w-full px-3 py-2 border rounded-lg"
                                />
                              </div>
                              <div>
                                <label className="block text-sm font-medium mb-1">Descrição (opcional)</label>
                                <textarea
                                  value={documentDescription}
                                  onChange={(e) => setDocumentDescription(e.target.value)}
                                  placeholder="Informações adicionais..."
                                  className="w-full px-3 py-2 border rounded-lg resize-none"
                                  rows={2}
                                />
                              </div>
                              <button
                                onClick={() => handleDocumentUpload(claim.id, claim.business_id)}
                                disabled={!documentFile || uploading}
                                className="w-full px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 disabled:opacity-50 flex items-center justify-center gap-2"
                              >
                                {uploading ? (
                                  <>
                                    <Loader2 className="w-4 h-4 animate-spin" />
                                    Enviando...
                                  </>
                                ) : (
                                  <>
                                    <Upload className="w-4 h-4" />
                                    Enviar documento
                                  </>
                                )}
                              </button>
                            </div>
                          </div>
                        )}

                        {/* Actions */}
                        <div className="flex gap-2">
                          <button
                            onClick={() => navigate(`/negocio/${claim.business_id}`)}
                            className="flex-1 px-4 py-2 border border-[var(--border)] rounded-lg hover:bg-gray-50 flex items-center justify-center gap-2"
                          >
                            <Building className="w-4 h-4" />
                            Ver negócio
                          </button>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {/* Info */}
        <div className="mt-8 bg-blue-50 border border-blue-200 rounded-xl p-6">
          <h3 className="font-bold text-blue-900 mb-2">Como funciona a verificação?</h3>
          <ul className="text-sm text-blue-800 space-y-2">
            <li><strong>Verificação simples:</strong> Código enviado para o WhatsApp do negócio</li>
            <li><strong>Verificação por documento:</strong> Necessária quando já existe proprietário registrado</li>
            <li><strong>Documentos aceitos:</strong> CNPJ, Contrato Social, Alvará</li>
            <li><strong>Prazo de análise:</strong> Até 3 dias úteis para documentos</li>
          </ul>
        </div>
      </div>
    </div>
  );
}
